/**
 * Admin → Email → Outbox
 *
 * Everything queued across every record, in one list. The per-record panels on
 * a client or lead page answer "what is going out to *them*"; this answers "what
 * is this system about to send", which is the question you want answered before
 * you change a template, go on holiday, or wonder why a client got an e-mail.
 *
 * It is also where a persistent failure becomes visible. The runner retries a
 * message and then gives up quietly — quietly on purpose, because a background
 * job that shouts is one people stop reading — so the record of it giving up has
 * to be somewhere a person looks.
 *
 * Finished messages are behind a toggle rather than shown by default, for the
 * same reason as the per-record panel: a year of sent rows would bury the two
 * lines that still matter.
 */
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  listScheduledMessages,
  updateScheduledMessage,
  deleteScheduledMessage,
} from '../../services/crm'
import { getNotificationSettings } from '../../services/admin'
import { formatLocalDateTime } from '../../utils/formatDateTime'
import type { ScheduledMessage, ScheduledMessageStatus } from '../../types'

const STATUS_STYLES: Record<ScheduledMessageStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-700',
  paused: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
}

const STATUS_LABELS: Record<ScheduledMessageStatus, string> = {
  scheduled: 'Waiting',
  paused: 'Paused',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

function targetLink(m: ScheduledMessage) {
  if (m.client_id) return `/crm/clients/${m.client_id}`
  if (m.lead_id) return `/crm/leads/${m.lead_id}`
  return null
}

export default function AdminOutboxPage() {
  const [messages, setMessages] = useState<ScheduledMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [showFinished, setShowFinished] = useState(false)
  const [error, setError] = useState('')
  const [working, setWorking] = useState<number | null>(null)
  /**
   * Whether client e-mail is switched on at all.
   *
   * The queue runner checks this before it sends anything, and when it is off it
   * leaves every due message sitting there — a pause, not a cancellation, which
   * is the right behaviour. What was missing is any sign of it here: two test
   * messages staged on 2026-09-05 were going to sit at "Waiting" past their send
   * time with nothing on the page to explain why, and the switch itself lives
   * under a tab called "Automatic Reminders", which does not obviously govern a
   * message somebody scheduled by hand.
   *
   * `null` while unknown, so the banner never flashes on during the first load.
   */
  const [clientEmailOn, setClientEmailOn] = useState<boolean | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setMessages(await listScheduledMessages({ include_finished: showFinished, limit: 200 }))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the outbox')
    } finally {
      setLoading(false)
    }
  }, [showFinished])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    getNotificationSettings()
      .then((s) => setClientEmailOn(s.notification_enabled))
      // Non-critical: without it the banner simply does not appear, which is the
      // behaviour this page had before.
      .catch(() => {})
  }, [])

  async function change(id: number, status: 'scheduled' | 'paused' | 'cancelled') {
    setWorking(id)
    setError('')
    try {
      await updateScheduledMessage(id, status)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the message')
    } finally {
      setWorking(null)
    }
  }

  async function remove(id: number) {
    setWorking(id)
    setError('')
    try {
      await deleteScheduledMessage(id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the message')
    } finally {
      setWorking(null)
    }
  }

  return (
    <div>
      {clientEmailOn === false && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">Nothing in this queue is being sent.</span>{' '}
          Client e-mail is switched off, so every message below stays where it is until it is
          switched back on — nothing is lost and nothing is cancelled.{' '}
          <Link
            to="/admin/email?tab=reminders"
            className="underline hover:text-amber-950"
          >
            Turn client e-mail on
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <p className="text-sm text-gray-600 max-w-2xl">
          Messages queued to go out later, across every client and lead. A message is sent at the
          time it was set for, or within about fifteen minutes of it.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
          <input
            type="checkbox"
            checked={showFinished}
            onChange={(e) => setShowFinished(e.target.checked)}
          />
          Show finished
        </label>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-10">
            {showFinished ? 'No messages have been scheduled yet.' : 'Nothing is waiting to go out.'}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">To</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Message</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Next send</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Repeat</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {messages.map((m) => {
                const href = targetLink(m)
                return (
                  <tr key={m.id} className="align-top">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_STYLES[m.status]}`}>
                        {STATUS_LABELS[m.status]}
                      </span>
                      {m.last_error && (
                        <p className={`text-xs mt-1 max-w-xs ${
                          m.status === 'cancelled' ? 'text-gray-500' : 'text-red-500'
                        }`}>
                          {m.last_error}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {href ? (
                        <Link to={href} className="text-primary-600 hover:underline">
                          {m.target_label}
                        </Link>
                      ) : (
                        <span className="text-gray-900">{m.target_label}</span>
                      )}
                      <div className="text-xs text-gray-500">{m.to_email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-900">{m.template_name ?? '—'}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-700">
                      {m.next_send_at ? formatLocalDateTime(m.next_send_at) : '—'}
                      {m.sent_count > 0 && (
                        <div className="text-xs text-gray-500">
                          sent {m.sent_count}× · last {formatLocalDateTime(m.last_sent_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{m.recurrence_summary}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="flex gap-3 justify-end">
                        {m.status === 'scheduled' && (
                          <button
                            type="button"
                            className="tap text-xs text-primary-600 hover:underline"
                            disabled={working === m.id}
                            onClick={() => change(m.id, 'paused')}
                          >
                            Pause
                          </button>
                        )}
                        {m.status === 'paused' && (
                          <button
                            type="button"
                            className="tap text-xs text-primary-600 hover:underline"
                            disabled={working === m.id}
                            onClick={() => change(m.id, 'scheduled')}
                          >
                            Resume
                          </button>
                        )}
                        {(m.status === 'scheduled' || m.status === 'paused') && (
                          <button
                            type="button"
                            className="tap text-xs text-red-600 hover:underline"
                            disabled={working === m.id}
                            onClick={() => change(m.id, 'cancelled')}
                          >
                            Cancel
                          </button>
                        )}
                        {(m.status === 'sent' || m.status === 'failed' || m.status === 'cancelled') && (
                          <button
                            type="button"
                            className="tap text-xs text-gray-500 hover:underline"
                            disabled={working === m.id}
                            onClick={() => remove(m.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
